import axios from "axios";
import useForm from "../../../hooks/useForm";
import OrvIcon from "imagesUwKluis/svg/orv.svg";
import {cloneDeep} from "lodash";
import {Fragment, useContext, useEffect, useRef, useState} from "react";
import {checkAndFormatDates} from "../../../utils/dateHelpers";
import AddItemBox from "../../shared/AddItemBox";
import Button from "../../shared/Button";
import Header from "../../shared/Header";
import {Box, Cluster, Flex, Stack} from "../../shared/layouts";
import InsuranceBlock from "./InsuranceBlock";
import RemoveIcon from "imagesUwKluis/svg/close.svg";
import {useTheme} from '@emotion/react';
import {useMediaQuery} from "@dnhb/node_package";
import MessagebarContext from "../../../context/MessagebarContext";
import UserContext from "../../../context/UserContext";
import PreventUnload from "../../shared/PreventUnload";
import ConfirmModal from "../../shared/ConfirmModal";
import Loader from "../../shared/Loader";
import WhiteLabelContext from "../../../context/WhiteLabelContext";
import {useTranslation} from "react-i18next";
import ReportIncorrectDataLink from "../../shared/ReportIncorrectDataLink";

export const coverageObj = {
  policy_holder: "",
  covered: "", //partner or applicant
  coverage: null,
  start_date: "",
  end_date: "",
  coverage_type: "", //"constant", "linear_decrease", "annuity_decrease"
};

const insuranceObj = {
  uuid: "",
  insurance_type: "",
  insurance_subtype: "",
  insurance_company: null,
  policy_number: "",
  pawned: false,
  premium: {
    amount: null,
    period: "",
    duration_in_months: null,
  },
  coverages: [coverageObj],
  advisor: {},
};

const OrvPage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [insurances, , setInsurances] = useForm([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [meta, setMeta] = useState({});
  const [errors, setErrors] = useState({});
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: "",
    text: "",
    onCancel: () => {},
    onSubmit: () => {},
  });

  const mountedRef = useRef(true);
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? "56.25rem"})`
  );
  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  const { canEditOrv } = useContext(WhiteLabelContext);
  const { setChangedDataWithoutSaving } = useContext(UserContext);

  const hasInsurances = insurances.length > 0;

  useEffect(() => {
    setChangedDataWithoutSaving(false);
  }, []);

  useEffect(() => {
    axios('/dossier/insurance')
      .then(({ data: { insurances = [], meta = {} } }) => {
        if (!mountedRef.current) return null;

        setInsurances(checkAndFormatDates(insurances));
        setInsuranceCompanies(meta.providers);
        setMeta(meta);
      })
      .catch((err) => {
        setAlertMessage();
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const saveInsurance = (e) => {
    e.preventDefault();
    setIsLoading(true);
    axios
      .post("/dossier/insurance", {
        insurances: checkAndFormatDates(insurances, true),
      })
      .then(() => {
        setSuccesMessage();
        setErrors({});
        setChangedDataWithoutSaving(false);
        if (insurances.length === 0) setShowSaveButton(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setAlertMessage();
        setIsLoading(false);
        const errors = err.response?.data?.errors;
        if (errors) {
          setErrors(() =>
            Object.keys(errors).reduce(
              (acc, val) => ({
                ...acc,
                [val.replace(/^[^.]*[.]/, "")]: errors[val],
              }),
              {}
            )
          );
        }
      });
  };

  const setInsurance = (e, indexInsurance) => {
    const { name, value } = e.target;
    const copyInsurances = cloneDeep(insurances);
    copyInsurances[indexInsurance][name] = value;
    setInsurances(copyInsurances, false);
  };

  const setCoverage = (e, indexInsurance, indexCoverage) => {
    const { name, value } = e.target;
    const copyInsurances = cloneDeep(insurances);
    copyInsurances[indexInsurance].coverages[indexCoverage][name] = value;
    setInsurances(copyInsurances, false);
  };

  const addCoverage = (indexInsurance) => {
    const copyInsurances = cloneDeep(insurances);
    copyInsurances[indexInsurance].coverages = [
      ...(copyInsurances[indexInsurance].coverages || []),
      coverageObj
    ];
    setInsurances(copyInsurances, false);
  };

  const addInsurance = () => {
    const copyInsurances = cloneDeep(insurances);
    copyInsurances.push(insuranceObj);
    setInsurances(copyInsurances, false);
  };

  const removeInsurance = (indexInsurance) => {
    setModal({
      isOpen: true,
      title: t('Overlijdensrisicoverzekering verwijderen'),
      text: t('Weet je zeker dat je deze overlijdensrisicoverzekering (ORV) wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'),
      onCancel: () => {
        setModal({ isOpen: false });
      },
      onConfirm: () => {
        const copyInsurances = cloneDeep(insurances);
        copyInsurances.splice(indexInsurance, 1);
        setInsurances(copyInsurances, false);
        setChangedDataWithoutSaving(false);
        setModal({ isOpen: false });
        setChangedDataWithoutSaving(true);
        setShowSaveButton(true);
      },
    });
  };

  const removeCoverage = (indexInsurance, indexCoverage) => {
    setModal({
      isOpen: true,
      title: t("Verzekerde verwijderen"),
      text: t("Weet je zeker dat je deze verzekerde wilt verwijderen? Dit kan niet ongedaan worden gemaakt."),
      onCancel: () => {
        setModal({ isOpen: false });
      },
      onConfirm: () => {
        const copyInsurances = cloneDeep(insurances);
        copyInsurances[indexInsurance].coverages.splice(indexCoverage, 1);
        setInsurances(copyInsurances, false);
        setModal({ isOpen: false });
      },
    });
  };

  const InsuranceHeader = ({ insuranceIndex }) => {
    const isMainHeader = insuranceIndex === 0;
    const showCount = insurances.length > 1;
    const insuranceCount = insuranceIndex + 1;

    return (
      <Header
        title={
          <Cluster hGap="l">
            <span>
              {t('Overlijdensrisicoverzekering (ORV) {{index}}', {index: showCount ? insuranceCount : ''})}
            </span>
            {hasInsurances && canEditOrv && (
              <RemoveIcon
                css={(theme) => ({
                  fill: theme?.colors?.alert || 'red',
                  cursor: 'pointer',
                  margin: '4px',
                  width: '32px',
                  height: '32px',
                })}
                onClick={() => removeInsurance(insuranceIndex)}
              />
            )}
          </Cluster>
        }
        icon={<OrvIcon height="2rem" width="2rem" />}
        variant="page"
        button={
          isMainHeader &&
          hasInsurances &&
          isMinDesktop &&
          canEditOrv && (
            <Button type="submit">{t('Opslaan')}</Button>
          )
        }
      />
    );
  };

  return (
    <PreventUnload>
      {isLoading && <Loader />}
      <ConfirmModal
        isOpen={modal.isOpen}
        title={modal.title}
        text={modal.text}
        onCancel={modal.onCancel}
        onConfirm={modal.onConfirm}
      />
      <Stack
        as="form"
        onSubmit={saveInsurance}
        space="xl"
        width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}
      >
        <InsuranceHeader insuranceIndex={0} />
        {insurances.map((insurance, indexInsurance) => (
            <Fragment key={indexInsurance}>
              {indexInsurance > 0 && (
                <InsuranceHeader insuranceIndex={indexInsurance} />
              )}
              <InsuranceBlock
                {...{
                  insurance,
                  meta,
                  insuranceCompanies,
                  indexInsurance,
                  removable: insurances.length > 1,
                  setCoverage,
                  setInsurance: (e) => setInsurance(e, indexInsurance),
                  addCoverage: () => addCoverage(indexInsurance),
                  removeCoverage,
                  errors,
                  canEditOrv,
                }}
              />
              {insurances.length > (indexInsurance + !canEditOrv) && (
                <Box borderTop="default" />
              )}
            </Fragment>
          ))}
        {!canEditOrv && (
          <ReportIncorrectDataLink />
        )}
        {canEditOrv && (
          <>
            <AddItemBox
              title={t("Overlijdensrisicoverzekering toevoegen")}
              onClick={addInsurance}
            />
            {hasInsurances || showSaveButton && (
              <Flex justifyContent="flex-end">
                <Button type="submit">{t('Opslaan')}</Button>
              </Flex>
            )}
          </>
        )}
      </Stack>
    </PreventUnload>
  );
};

export default OrvPage;
